import { useState, useMemo } from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from "@tanstack/react-table";
import { rankItem } from "@tanstack/match-sorter-utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faMagnifyingGlass,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const fuzzyFilter = (row, columnId, value, addMeta) => {
  const itemRank = rankItem(row.getValue(columnId), value);
  addMeta({ itemRank });
  return itemRank.passed;
};
fuzzyFilter.autoRemove = (val) => !val;

export default function DataTable({
  columns,
  data,
  filterPlaceholder = "Search...",
  filterSlot,
  totalLabel = "Total",
  emptyMessage = "No results found.",
  pageSize = 10,
  serverPagination = false,
  serverPageCount = 1,
  serverTotalCount,
  onServerPageChange,
}) {
  const [globalFilter, setGlobalFilter] = useState("");
  const [sorting, setSorting] = useState([]);
  const [pagination, setPagination] = useState({ pageIndex: 0, pageSize });

  const handlePaginationChange = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    if (serverPagination && next.pageIndex !== pagination.pageIndex) {
      onServerPageChange?.(next.pageIndex + 1);
    }
    setPagination(next);
  };

  const table = useReactTable({
    data,
    columns,
    state: { globalFilter, sorting, pagination },
    onGlobalFilterChange: setGlobalFilter,
    onSortingChange: setSorting,
    onPaginationChange: handlePaginationChange,
    globalFilterFn: fuzzyFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: serverPagination,
    pageCount: serverPagination ? serverPageCount : undefined,
  });

  const totalCount = serverPagination
    ? (serverTotalCount ?? 0)
    : table.getFilteredRowModel().rows.length;
  const { pageIndex } = table.getState().pagination;
  const pageCount = table.getPageCount();

  return (
    <div className="p-6">
      {/* Search + Filter row */}
      <div className="flex items-center gap-3 mb-6">
        <div className="flex items-center gap-3 border border-gray-300 rounded-full px-4 py-[1px] flex-1">
          <FontAwesomeIcon icon={faMagnifyingGlass} className="text-gray-400" size="sm" />
          <input
            type="text"
            placeholder={filterPlaceholder}
            value={globalFilter}
            onChange={(e) => {
              setGlobalFilter(e.target.value);
              table.setPageIndex(0);
            }}
            className="flex-1 outline-none border-none bg-transparent text-sm text-gray-700 placeholder-gray-400"
          />
        </div>
        {filterSlot}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-gray-200">
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="bg-gray-50">
                {headerGroup.headers.map((header) => {
                  const canSort = header.column.getCanSort();
                  const sorted = header.column.getIsSorted();
                  return (
                    <th
                      key={header.id}
                      onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                      className={`px-3 py-3 text-left border border-gray-200 !text-sm !font-semibold text-orange whitespace-nowrap ${canSort ? "cursor-pointer select-none" : ""}`}
                      style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    >
                      {header.isPlaceholder ? null : (
                        <span className="flex items-center gap-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {canSort && (
                            <span className={`ml-1 ${sorted ? "text-orange" : "text-orange opacity-30"}`}>
                              {sorted === "asc" ? "↑" : sorted === "desc" ? "↓" : "↑↓"}
                            </span>
                          )}
                        </span>
                      )}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.length > 0 ? (
              table.getRowModel().rows.map((row) => (
                <tr key={row.id} className="hover:bg-gray-50">
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className="px-3 py-2 border border-gray-200 !text-sm text-gray-800 text-left"
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="py-8 text-center text-sm text-gray-400 border border-gray-200"
                >
                  {emptyMessage}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
        <p className="!text-sm text-gray-500">
          {totalLabel}: {totalCount}
        </p>
        <div className="flex items-center gap-2">
          <button
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faChevronLeft} size="xs" />
          </button>
          <span className="!text-sm text-gray-600">
            {pageIndex + 1} of {Math.max(1, pageCount)}
          </span>
          <button
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            className="w-7 h-7 flex items-center justify-center border border-gray-300 rounded text-gray-600 hover:bg-gray-100 disabled:opacity-40"
          >
            <FontAwesomeIcon icon={faChevronRight} size="xs" />
          </button>
        </div>
      </div>
    </div>
  );
}
